import { QueryClient } from '@tanstack/react-query'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { get, set, del } from 'idb-keyval'

// Custom storage adapter using IndexedDB via idb-keyval
const idbStorage = {
  getItem: async (key: string): Promise<string | null> => {
    try {
      const value = await get(key)
      return value ?? null
    } catch {
      return null
    }
  },
  setItem: async (key: string, value: string): Promise<void> => {
    try {
      await set(key, value)
    } catch {
      // Ignore storage errors
    }
  },
  removeItem: async (key: string): Promise<void> => {
    try {
      await del(key)
    } catch {
      // Ignore storage errors
    }
  },
}

// Create a wrapper that handles async operations synchronously for the persister
// Note: The sync storage persister expects sync operations, but we can work around this
// by using localStorage as a fallback and IndexedDB for larger data
const hybridStorage = {
  getItem: (key: string): string | null => {
    try {
      // Try localStorage first (sync)
      const value = localStorage.getItem(key)
      if (value) return value

      // Queue IndexedDB read for next time
      idbStorage.getItem(key).then(idbValue => {
        if (idbValue && !localStorage.getItem(key)) {
          localStorage.setItem(key, idbValue)
        }
      })

      return null
    } catch {
      return null
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      // Write to localStorage for sync access
      localStorage.setItem(key, value)
      // Also write to IndexedDB for persistence
      idbStorage.setItem(key, value)
    } catch {
      // Storage might be full, try IndexedDB only
      idbStorage.setItem(key, value)
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key)
      idbStorage.removeItem(key)
    } catch {
      // Ignore
    }
  },
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
      retry: 3,
      refetchOnWindowFocus: false, // Mobile doesn't need this
      // Network mode for offline support
      networkMode: 'offlineFirst',
    },
    mutations: {
      // Mutations should retry when back online
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      networkMode: 'offlineFirst',
    },
  },
})

// Create persister for offline cache
export const persister = createSyncStoragePersister({
  storage: hybridStorage,
  key: 'roadeo_query_cache',
  // Throttle writes to prevent excessive storage operations
  throttleTime: 1000,
})

/**
 * Check if we're currently online
 */
export function isOnline(): boolean {
  return navigator.onLine
}

/**
 * Hook to track online status
 */
export function useOnlineStatus(): boolean {
  if (typeof window === 'undefined') return true
  return navigator.onLine
}
